import { AnswerBlock } from '../../models/message';

/**
 * Typing out an answer draft. The server sends a draft a whole validated segment at a time; this only reveals what it
 * already sent, character by character in block order, so the answer reads as typed rather than jumping a sentence at
 * a time. Nothing here invents or holds back content for long:
 * - a steady base rate, sped up so every piece is on screen at most `TYPING_CATCH_UP_MS` after it arrived (typing never
 *   falls behind the stream, and a tab that comes back shows everything at once);
 * - what is on screen is never typed again: a newer draft keeps it as far as it still reads the same, and types the rest
 *   (the server restarting the answer shows the common start);
 * - with reduced motion, everything shows at once, a draft at a time.
 */

/** The base typing rate, in characters per second. */
export const TYPING_BASE_CPS = 80;
/** The longest a piece of a draft waits after it arrived before it is all on screen. */
export const TYPING_CATCH_UP_MS = 500;

export interface Reveal {
   /** The blocks being typed out: the latest draft. */
   target: AnswerBlock[];
   /** How many characters of `target` are on screen, in block order (fractional between frames). */
   revealed: number;
   /** What must be on screen how soon: one entry per draft that brought text, `end` characters within `inMs`. */
   due: { end: number; inMs: number }[];
}

export function textLength(blocks: AnswerBlock[]): number {
   return blocks.reduce((length, block) => length + block.text.length, 0);
}

/** How many characters, in block order, two drafts share from the start (blocks of another kind share nothing). */
export function commonLength(a: AnswerBlock[], b: AnswerBlock[]): number {
   let length = 0;
   for (let i = 0; i < Math.min(a.length, b.length); i++) {
      if (a[i].kind !== b[i].kind) return length;
      const x = a[i].text;
      const y = b[i].text;
      if (x === y) {
         length += x.length;
         continue;
      }
      let same = 0;
      while (same < x.length && same < y.length && x[same] === y[same]) same++;
      return length + same;
   }
   return length;
}

/** The first draft: nothing on screen yet (everything with reduced motion). */
export function startReveal(target: AnswerBlock[], reduceMotion = false): Reveal {
   return retarget({ target: [], revealed: 0, due: [] }, target, reduceMotion);
}

/**
 * A newer draft. What is on screen stays as far as it still reads the same; the rest is typed, due within
 * `TYPING_CATCH_UP_MS`. A restart never empties a page already on screen: its first character shows at once.
 */
export function retarget(reveal: Reveal, target: AnswerBlock[], reduceMotion = false): Reveal {
   const total = textLength(target);
   if (reduceMotion) return { target, revealed: total, due: [] };

   const common = commonLength(reveal.target, target);
   let revealed = Math.min(reveal.revealed, common);
   if (reveal.revealed >= 1 && revealed < 1) revealed = Math.min(1, total);

   const due = reveal.due.map((d) => ({ end: Math.min(d.end, common), inMs: d.inMs })).filter((d) => d.end > revealed);
   if (total > revealed && !due.some((d) => d.end >= total)) due.push({ end: total, inMs: TYPING_CATCH_UP_MS });
   return { target, revealed, due };
}

/** `elapsedMs` more of typing: the base rate, or faster where a piece would otherwise be late. */
export function advanceReveal(reveal: Reveal, elapsedMs: number): Reveal {
   const total = textLength(reveal.target);
   if (reveal.revealed >= total || elapsedMs <= 0) return reveal;

   let next = reveal.revealed + (TYPING_BASE_CPS * elapsedMs) / 1000;
   reveal.due.forEach(({ end, inMs }) => {
      // On time exactly: the remaining characters of each piece shrink in step with the time left for it.
      const onTime = elapsedMs >= inMs ? end : reveal.revealed + ((end - reveal.revealed) * elapsedMs) / inMs;
      next = Math.max(next, onTime);
   });
   const revealed = Math.min(total, next);
   const due = reveal.due.map((d) => ({ end: d.end, inMs: d.inMs - elapsedMs })).filter((d) => d.end > revealed);
   return { target: reveal.target, revealed, due };
}

export function isTyping(reveal: Reveal): boolean {
   return reveal.revealed < textLength(reveal.target);
}

const isHighSurrogate = (code: number) => code >= 0xd800 && code <= 0xdbff;

/**
 * The blocks on screen with `revealed` characters typed: whole blocks, then the start of the one being typed. A value
 * being typed is bold for its typed part, at the very place the whole value will be (`valueAt`).
 */
export function visibleBlocks(target: AnswerBlock[], revealed: number): AnswerBlock[] {
   let left = Math.floor(revealed);
   const visible: AnswerBlock[] = [];
   for (const block of target) {
      if (left <= 0) break;
      if (left >= block.text.length) {
         visible.push(block);
         left -= block.text.length;
         continue;
      }
      // Never split a character made of two code units (an emoji): show it whole a frame early.
      const shown = isHighSurrogate(block.text.charCodeAt(left - 1)) ? left + 1 : left;
      const partial: AnswerBlock = { kind: block.kind, text: block.text.slice(0, shown) };
      const at = block.value ? block.text.indexOf(block.value) : -1;
      if (block.value && at >= 0 && shown > at) {
         partial.value = block.value.slice(0, shown - at);
         partial.valueAt = at;
      }
      visible.push(partial);
      break;
   }
   return visible;
}
